<template>
  <div class="helpdesk-chatbot d-none">
    <transition name="chat-panel">
      <section v-if="isOpen" class="helpdesk-chatbot-panel" aria-label="Helpdesk chat">
        <header class="helpdesk-chatbot-header">
          <div>
            <span class="helpdesk-chatbot-kicker">Helpdesk</span>
            <h2>Chat with support</h2>
          </div>
          <button type="button" class="helpdesk-chatbot-icon" title="Close chat" aria-label="Close chat" @click="toggleChat">
            <i class="ri-close-line"></i>
          </button>
        </header>

        <div v-if="!conversation.referenceNumber" class="helpdesk-chatbot-start">
          <input v-model.trim="requesterName" class="form-control" placeholder="Your name" />
          <input v-model.trim="requesterContact" class="form-control" placeholder="Email or phone" />
          <textarea v-model.trim="draftMessage" class="form-control" rows="4" placeholder="How can helpdesk assist you?"></textarea>
          <input ref="startFileInput" type="file" class="d-none" accept=".jpg,.jpeg,.png,.gif,.pdf" multiple @change="handleFileSelect" />
          <button type="button" class="btn btn-light helpdesk-chatbot-attach" :disabled="isUploading" @click="$refs.startFileInput.click()">
            <i class="ri-attachment-2 me-1"></i>{{ isUploading ? 'Uploading...' : 'Attach screenshot' }}
          </button>
          <div v-if="attachments.length" class="helpdesk-chatbot-attachments">
            <div v-for="(attachment, index) in attachments" :key="attachment.fileReference || index" class="helpdesk-chatbot-attachment">
              <i class="ri-image-line"></i>
              <span>{{ attachment.fileName }}</span>
              <button type="button" title="Remove attachment" @click="removeAttachment(index)">
                <i class="ri-close-line"></i>
              </button>
            </div>
          </div>
          <button type="button" class="btn btn-primary w-100" :disabled="!canStart || isSending" @click="startConversation">
            <i class="ri-send-plane-2-line me-1"></i> Start chat
          </button>
        </div>

        <template v-else>
          <div class="helpdesk-chatbot-status">
            <span>{{ conversation.status || 'Open' }}</span>
            <small>{{ conversation.referenceNumber }}</small>
          </div>

          <div ref="messagesContainer" class="helpdesk-chatbot-messages">
            <div
              v-for="message in messages"
              :key="message.id || message.referenceNumber"
              class="helpdesk-chatbot-message"
              :class="message.senderType === 'CUSTOMER' ? 'is-user' : 'is-assistant'"
            >
              <div class="helpdesk-chatbot-avatar">
                <i :class="message.senderType === 'CUSTOMER' ? 'ri-user-3-line' : 'ri-customer-service-2-line'"></i>
              </div>
              <div class="helpdesk-chatbot-bubble">
                <strong>{{ message.senderName || senderLabel(message.senderType) }}</strong>
                <p>{{ message.message }}</p>
                <div v-if="message.attachments && message.attachments.length" class="helpdesk-chatbot-message-files">
                  <a
                    v-for="attachment in message.attachments"
                    :key="attachment.fileReference || attachment.fileUrl"
                    :href="attachment.fileUrl"
                    target="_blank"
                    rel="noopener"
                    class="helpdesk-chatbot-message-file">
                    <i :class="fileIcon(attachment.fileName)"></i>
                    <span>{{ attachment.fileName || 'Attachment' }}</span>
                  </a>
                </div>
              </div>
            </div>
            <div v-if="!messages.length" class="helpdesk-chatbot-empty">
              Your conversation has started. A helpdesk officer will respond here.
            </div>
          </div>

          <form class="helpdesk-chatbot-form" @submit.prevent="sendMessage">
            <input ref="messageFileInput" type="file" class="d-none" accept=".jpg,.jpeg,.png,.gif,.pdf" multiple @change="handleFileSelect" />
            <textarea
              ref="messageInput"
              v-model.trim="draftMessage"
              class="form-control"
              rows="2"
              placeholder="Type your message..."
              :disabled="isSending || conversation.status === 'Closed'"
              @keydown.enter.exact.prevent="sendMessage"
            ></textarea>
            <div class="helpdesk-chatbot-actions">
              <button type="button" class="btn btn-light helpdesk-chatbot-action" title="Attach screenshot" aria-label="Attach screenshot" :disabled="isUploading || conversation.status === 'Closed'" @click="$refs.messageFileInput.click()">
                <i class="ri-attachment-2"></i>
              </button>
              <button type="submit" class="btn btn-primary helpdesk-chatbot-send" title="Send message" aria-label="Send message" :disabled="!canSendMessage || isSending">
                <i class="ri-send-plane-2-line"></i>
              </button>
            </div>
          </form>
          <div v-if="attachments.length" class="helpdesk-chatbot-attachments is-composer">
            <div v-for="(attachment, index) in attachments" :key="attachment.fileReference || index" class="helpdesk-chatbot-attachment">
              <i :class="fileIcon(attachment.fileName)"></i>
              <span>{{ attachment.fileName }}</span>
              <button type="button" title="Remove attachment" @click="removeAttachment(index)">
                <i class="ri-close-line"></i>
              </button>
            </div>
          </div>
        </template>
      </section>
    </transition>

    <button type="button" class="helpdesk-chatbot-fab" :title="isOpen ? 'Close helpdesk chat' : 'Open helpdesk chat'" :aria-label="isOpen ? 'Close helpdesk chat' : 'Open helpdesk chat'" @click="toggleChat">
      <i :class="isOpen ? 'ri-close-line' : 'ri-chat-3-line'"></i>
    </button>
  </div>
</template>

<script>
import axios from "axios";
import {
  GET_CHAT_CONVERSATION,
  SEND_CHAT_MESSAGE,
  START_CHAT_CONVERSATION,
} from "@/helpers/helpdesk-helpers/helpdesk-endpoints";
import { UPLOAD_FILE } from "@/helpers/utility-endpoints/utility-endpoints";

export default {
  name: "HelpdeskChatbot",
  props: {
    portal: {
      type: String,
      default: "backoffice",
    },
  },
  data() {
    return {
      isOpen: false,
      isSending: false,
      poller: null,
      draftMessage: "",
      requesterName: "",
      requesterContact: "",
      conversation: {},
      messages: [],
      attachments: [],
      isUploading: false,
    };
  },
  computed: {
    storageKey() {
      return `zppa-helpdesk-chat-${this.portal}`;
    },
    canStart() {
      return this.draftMessage && this.requesterName && this.requesterContact && !this.isUploading;
    },
    canSendMessage() {
      return (this.draftMessage || this.attachments.length) && !this.isUploading && !this.isSending;
    },
  },
  mounted() {
    this.hydrateUser();
    this.restoreConversation();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    toggleChat() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.fetchConversation();
        this.startPolling();
        this.$nextTick(this.scrollToBottom);
      } else {
        this.stopPolling();
      }
    },
    hydrateUser() {
      const firstName = sessionStorage.getItem("firstName") || "";
      const lastName = sessionStorage.getItem("lastName") || "";
      this.requesterName = `${firstName} ${lastName}`.trim() || sessionStorage.getItem("username") || "";
      this.requesterContact = sessionStorage.getItem("email") || sessionStorage.getItem("username") || sessionStorage.getItem("phone") || "";
    },
    restoreConversation() {
      try {
        const saved = JSON.parse(localStorage.getItem(this.storageKey) || "{}");
        if (saved.referenceNumber && saved.clientToken) {
          this.conversation = saved;
        }
      } catch (error) {
        localStorage.removeItem(this.storageKey);
      }
    },
    async startConversation() {
      if (!this.canStart || this.isSending) {
        return;
      }
      this.isSending = true;
      try {
        const response = await axios.post(START_CHAT_CONVERSATION, this.basePayload());
        this.applyConversation(response.data.data);
        this.draftMessage = "";
        this.attachments = [];
        this.startPolling();
      } finally {
        this.isSending = false;
      }
    },
    async sendMessage() {
      if (!this.canSendMessage || !this.conversation.referenceNumber) {
        return;
      }
      this.isSending = true;
      try {
        const response = await axios.post(SEND_CHAT_MESSAGE, {
          ...this.basePayload(),
          referenceNumber: this.conversation.referenceNumber,
          clientToken: this.conversation.clientToken,
        });
        this.applyConversation(response.data.data);
        this.draftMessage = "";
        this.attachments = [];
      } finally {
        this.isSending = false;
      }
    },
    async fetchConversation() {
      if (!this.conversation.referenceNumber || !this.conversation.clientToken) {
        return;
      }
      try {
        const response = await axios.post(GET_CHAT_CONVERSATION, {
          referenceNumber: this.conversation.referenceNumber,
          clientToken: this.conversation.clientToken,
        });
        this.applyConversation(response.data.data);
      } catch (error) {
        console.log(error);
      }
    },
    basePayload() {
      return {
        portal: this.portal,
        module: this.resolveModule(),
        routePath: this.$route.fullPath,
        subject: this.draftMessage ? this.draftMessage.slice(0, 80) : "Helpdesk chat",
        requesterName: this.requesterName,
        requesterContact: this.requesterContact,
        requesterUserId: sessionStorage.getItem("userId") || sessionStorage.getItem("id") || "",
        requesterRealmId: sessionStorage.getItem("realmId") || "",
        message: this.draftMessage,
        attachments: this.attachments,
      };
    },
    async handleFileSelect(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      if (!files.length) {
        return;
      }
      this.isUploading = true;
      try {
        for (const file of files) {
          if (!this.validAttachment(file)) {
            continue;
          }
          const uploaded = await this.uploadAttachment(file);
          this.attachments.push(uploaded);
        }
      } finally {
        this.isUploading = false;
      }
    },
    validAttachment(file) {
      const maxSize = 10 * 1024 * 1024;
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "application/pdf"];
      return file.size <= maxSize && allowedTypes.includes(file.type);
    },
    async uploadAttachment(file) {
      const formData = new FormData();
      formData.append("uploadFile", file);
      formData.append("shouldEncrypt", "N");
      formData.append("serviceCode", "HLP");
      formData.append("fileName", `HELPDESK_CHAT_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);

      const jwt = sessionStorage.getItem("jwt");
      const headers = { "Content-Type": "multipart/form-data" };
      if (jwt) {
        headers.Authorization = `Bearer ${jwt}`;
      }
      const response = await axios.post(UPLOAD_FILE, formData, { headers });
      const data = response.data.responseMessage || {};
      return {
        fileReference: data.fileReference,
        fileUrl: data.fileUrl,
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type,
      };
    },
    removeAttachment(index) {
      this.attachments.splice(index, 1);
    },
    fileIcon(fileName) {
      const extension = (fileName || "").split(".").pop().toLowerCase();
      if (extension === "pdf") {
        return "ri-file-pdf-line";
      }
      return "ri-image-line";
    },
    applyConversation(conversation) {
      if (!conversation) {
        return;
      }
      this.conversation = {
        referenceNumber: conversation.referenceNumber,
        clientToken: conversation.clientToken || this.conversation.clientToken,
        status: conversation.status,
      };
      this.messages = conversation.messages || [];
      localStorage.setItem(this.storageKey, JSON.stringify(this.conversation));
      this.$nextTick(this.scrollToBottom);
    },
    resolveModule() {
      return (this.$route.path || "").split("/").filter(Boolean)[0] || this.portal;
    },
    startPolling() {
      this.stopPolling();
      if (!this.conversation.referenceNumber) {
        return;
      }
      this.poller = setInterval(this.fetchConversation, 10000);
    },
    stopPolling() {
      if (this.poller) {
        clearInterval(this.poller);
        this.poller = null;
      }
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    senderLabel(senderType) {
      return senderType === "CUSTOMER" ? "You" : "Helpdesk";
    },
  },
};
</script>

<style scoped>
.helpdesk-chatbot {
    position: fixed;
    right: 240px;
    bottom: 24px;
    z-index: 1060
}

.helpdesk-chatbot-fab {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 58px;
    height: 58px;
    border: 0;
    border-radius: 50%;
    background: #387173;
    color: #fff;
    box-shadow: 0 12px 28px rgba(35, 48, 66, .28);
    font-size: 25px
}

.helpdesk-chatbot-panel {
    position: absolute;
    right: 0;
    bottom: 76px;
    display: flex;
    flex-direction: column;
    width: 380px;
    max-width: calc(100vw - 32px);
    height: 520px;
    max-height: calc(100vh - 116px);
    overflow: hidden;
    border: 1px solid rgba(140, 152, 164, .28);
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 20px 50px rgba(35, 48, 66, .24)
}

.helpdesk-chatbot-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 18px;
    border-bottom: 1px solid #eef0f3;
    background: #f8fafb
}

.helpdesk-chatbot-kicker {
    display: block;
    margin-bottom: 4px;
    color: #6c757d;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase
}

.helpdesk-chatbot-header h2 {
    margin: 0;
    color: #1f2d3d;
    font-size: 17px;
    font-weight: 700
}

.helpdesk-chatbot-icon {
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #5d6778;
    font-size: 20px
}

.helpdesk-chatbot-start {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px
}

.helpdesk-chatbot-status {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    border-bottom: 1px solid #eef0f3;
    color: #5d6778;
    font-size: 12px
}

.helpdesk-chatbot-messages {
    flex: 1;
    overflow-y: auto;
    padding: 18px;
    background: #f4f7f9
}

.helpdesk-chatbot-message {
    display: flex;
    gap: 10px;
    margin-bottom: 14px
}

.helpdesk-chatbot-message.is-user {
    flex-direction: row-reverse
}

.helpdesk-chatbot-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    border-radius: 50%;
    background: #e7f0f1;
    color: #387173;
    font-size: 17px
}

.helpdesk-chatbot-message.is-user .helpdesk-chatbot-avatar {
    background: #e8efff;
    color: #405189
}

.helpdesk-chatbot-bubble {
    max-width: 78%;
    padding: 10px 12px;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 3px 12px rgba(35, 48, 66, .06)
}

.helpdesk-chatbot-message.is-user .helpdesk-chatbot-bubble {
    background: #405189;
    color: #fff
}

.helpdesk-chatbot-bubble strong {
    display: block;
    margin-bottom: 4px;
    font-size: 11px
}

.helpdesk-chatbot-bubble p {
    margin: 0;
    color: inherit;
    font-size: 13px;
    line-height: 1.45;
    white-space: pre-line
}

.helpdesk-chatbot-form {
    display: grid;
    grid-template-columns:1fr 44px;
    gap: 10px;
    padding: 14px;
    border-top: 1px solid #eef0f3;
    background: #fff
}

.helpdesk-chatbot-form textarea, .helpdesk-chatbot-start textarea {
    resize: none;
    font-size: 13px
}

.helpdesk-chatbot-send {
    width: 44px;
    height: 44px;
    padding: 0;
    border-radius: 50%;
    font-size: 18px
}

.helpdesk-chatbot-empty {
    font-size: 13px;
    color: #748092;
    text-align: center;
    padding: 24px
}

.chat-panel-enter-active, .chat-panel-leave-active {
    transition: opacity .18s ease, transform .18s ease
}

.chat-panel-enter-from, .chat-panel-leave-to {
    opacity: 0;
    transform: translateY(12px)
}

@media (max-width: 575.98px) {
    .helpdesk-chatbot {
        right: 16px;
        bottom: 16px
    }

    .helpdesk-chatbot-panel {
        right: -4px;
        bottom: 72px;
        width: calc(100vw - 24px);
        height: min(540px, calc(100vh - 104px))
    }
}

.helpdesk-chatbot-attach {
    font-size: 13px
}

.helpdesk-chatbot-actions {
    display: flex;
    flex-direction: column;
    gap: 8px
}

.helpdesk-chatbot-action {
    width: 44px;
    height: 36px;
    padding: 0;
    border-radius: 50%;
    font-size: 16px
}

.helpdesk-chatbot-attachments {
    display: flex;
    flex-direction: column;
    gap: 6px
}

.helpdesk-chatbot-attachments.is-composer {
    padding: 0 14px 12px;
    background: #fff
}

.helpdesk-chatbot-attachment {
    display: grid;
    grid-template-columns:auto 1fr auto;
    align-items: center;
    gap: 8px;
    padding: 7px 9px;
    border: 1px solid #e6ebf1;
    border-radius: 6px;
    background: #f8fafb;
    color: #4b5563;
    font-size: 12px
}

.helpdesk-chatbot-attachment span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap
}

.helpdesk-chatbot-attachment button {
    border: 0;
    background: transparent;
    color: #748092
}

.helpdesk-chatbot-message-files {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px
}

.helpdesk-chatbot-message-file {
    display: grid;
    grid-template-columns:auto 1fr;
    gap: 6px;
    align-items: center;
    padding: 6px 8px;
    border-radius: 6px;
    background: rgba(255, 255, 255, .18);
    color: inherit;
    text-decoration: none;
    font-size: 12px
}

.helpdesk-chatbot-message-file span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap
}
</style>
